import { useCallback, useMemo, useState } from 'react'
import { FileText, Plus, Trash2 } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { useToast } from '@/providers/UiProviders'
import {
  fetchTemplates,
  createTemplate,
  deleteTemplate,
  BAN_STR as S,
  type TemplateRow,
  type RestrictionAction,
} from './strikesApi'

const ACTIONS: { value: RestrictionAction; label: string }[] = [
  { value: 'warn', label: S.actWarn },
  { value: 'strike', label: S.actStrike },
  { value: 'mute', label: S.actMute },
  { value: 'ban', label: S.actBan },
]

const inputCls =
  'rounded-md border border-border-default bg-surface-1 px-3 py-1.5 text-sm text-primary outline-none focus:border-border-strong'

/**
 * A8.09 — Cheklov shablonlari. JONLI `/admin/ban-templates` (000245): tayyor
 * jazo-ssenariylari (name/action/severity/duration/reason) — inline qo'shish +
 * o'chirish.
 */
export function RestrictionTemplatesScreen() {
  const { toast } = useToast()
  const [reloadKey, setReloadKey] = useState(0)
  const [name, setName] = useState('')
  const [action, setAction] = useState<RestrictionAction>('ban')
  const [severity, setSeverity] = useState('3')
  const [durationDays, setDurationDays] = useState('0')
  const [reasonText, setReasonText] = useState('')
  const [busy, setBusy] = useState(false)

  const add = async () => {
    if (!name.trim()) return
    setBusy(true)
    try {
      await createTemplate({
        name: name.trim(),
        action,
        severity: Math.min(5, Math.max(1, Number(severity) || 1)),
        duration_days: Math.max(0, Number(durationDays) || 0),
        reason_text: reasonText.trim(),
      })
      toast(S.addOk, { tone: 'success' })
      setName('')
      setReasonText('')
      setReloadKey((k) => k + 1)
    } catch {
      toast(S.failed, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }

  const columns = useMemo<AdminColumn<TemplateRow>[]>(
    () => [
      { key: 'name', header: S.name, type: 'text', width: 200 },
      {
        key: 'action',
        header: S.action,
        type: 'enum',
        width: 140,
        options: [
          { value: 'warn', label: S.actWarn, status: 'review' },
          { value: 'strike', label: S.actStrike, status: 'pending' },
          { value: 'mute', label: S.actMute, status: 'review' },
          { value: 'ban', label: S.actBan, status: 'banned' },
        ],
      },
      { key: 'severity', header: S.severity, type: 'number', width: 110, align: 'right' },
      { key: 'duration_days', header: S.duration, type: 'number', width: 100, align: 'right' },
      { key: 'reason_text', header: S.reasonText, type: 'longtext', minWidth: 200 },
    ],
    [],
  )

  const rowActions = useMemo<RowAction<TemplateRow>[]>(
    () => [
      {
        id: 'delete',
        label: S.remove,
        icon: <Trash2 size={14} />,
        danger: true,
        permission: PERMISSIONS.USERS_BAN,
        onSelect: async (r) => {
          try {
            await deleteTemplate(r.id)
            toast(S.removed, { tone: 'success' })
            setReloadKey((k) => k + 1)
          } catch {
            toast(S.failed, { tone: 'error' })
          }
        },
      },
    ],
    [toast],
  )

  const fetcher = useCallback(fetchTemplates, [])
  return (
    <>
      <PageHeader title={S.templates} screenId="A8.09" description={S.templatesNote} />
      <div className="flex flex-wrap items-end gap-2 p-4 pb-0">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={S.name}
          className={`${inputCls} w-44`}
        />
        <select
          value={action}
          onChange={(e) => setAction(e.target.value as RestrictionAction)}
          className={inputCls}
        >
          {ACTIONS.map((a) => (
            <option key={a.value} value={a.value}>
              {a.label}
            </option>
          ))}
        </select>
        <input
          type="number"
          value={severity}
          onChange={(e) => setSeverity(e.target.value)}
          placeholder={S.severity}
          className={`${inputCls} w-20`}
        />
        <input
          type="number"
          value={durationDays}
          onChange={(e) => setDurationDays(e.target.value)}
          placeholder={S.duration}
          className={`${inputCls} w-24`}
        />
        <input
          value={reasonText}
          onChange={(e) => setReasonText(e.target.value)}
          placeholder={S.reasonText}
          className={`${inputCls} min-w-[180px] flex-1`}
        />
        <button
          type="button"
          onClick={add}
          disabled={busy || !name.trim()}
          className="inline-flex items-center gap-1.5 rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
        >
          <Plus size={14} />
          {S.add}
        </button>
      </div>
      <DataTable<TemplateRow>
        key={reloadKey}
        screenId="A8.09"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        exportable
        emptyState={{ icon: <FileText size={28} strokeWidth={1.5} />, title: S.templates }}
      />
    </>
  )
}
